/**
 * Printer API Factory
 * 
 * @module api/printer/factory
 * @description 打印机管理模块的API工厂函数
 * @author YSFL Team
 * @date 2025-11-03
 */

import type {
  CreatePrintLogForm,
  CreatePrinterForm,
  PrintLog,
  PrintLogQueryParams,
  Printer,
  PrinterQueryParams,
  UpdatePrinterForm,
} from '../../types/printer'
import type {
  PrintTemplate,
  PrintTemplateQueryParams,
  CreatePrintTemplateForm,
  UpdatePrintTemplateForm,
} from '../../types/print-template'

import type { PaginationResult } from '../../types/api'
import type { IRequestClient } from '../core/client'
import { API_ROUTES } from '../routes'

export function createPrinterApi(client: IRequestClient) {
  return {
    // ========== 打印机管理 ==========

    /**
     * 获取打印机列表
     */
    getPrinterList(params?: PrinterQueryParams) {
      return client.get<PaginationResult<Printer>>(API_ROUTES.printer.list, { params })
    },

    /**
     * 获取打印机详情
     */
    getPrinterDetail(id: number) {
      return client.get<Printer>(API_ROUTES.printer.detail(id))
    },

    /**
     * 创建打印机
     */
    createPrinter(data: CreatePrinterForm) {
      return client.post<Printer>(API_ROUTES.printer.create, data)
    },

    /**
     * 更新打印机
     */
    updatePrinter(id: number, data: UpdatePrinterForm) {
      return client.put<Printer>(API_ROUTES.printer.printers(id), data)
    },

    /**
     * 删除打印机
     */
    deletePrinter(id: number) {
      return client.delete(API_ROUTES.printer.delete(id))
    },

    // ========== 打印记录 ==========

    /**
     * 获取打印记录列表
     */
    getPrintLogList(params?: PrintLogQueryParams) {
      return client.get<PaginationResult<PrintLog>>(API_ROUTES.printLog.list, { params })
    },

    /**
     * 获取打印记录详情
     */
    getPrintLogDetail(id: number) {
      return client.get<PrintLog>(API_ROUTES.printLog.detail(id))
    },

    /**
     * 上报打印记录
     *
     * 打印在客户端完成（蓝牙/浏览器），本接口仅留痕；上报失败不应影响打印主流程。
     */
    createPrintLog(data: CreatePrintLogForm) {
      return client.post<PrintLog>(API_ROUTES.printLog.create, data)
    },

    // ========== 打印模板管理 ==========

    /**
     * 获取打印模板列表
     */
    getTemplateList(params?: PrintTemplateQueryParams) {
      return client.get<PaginationResult<PrintTemplate>>(API_ROUTES.printTemplate.list, { params })
    },

    /**
     * 获取打印模板详情
     */
    getTemplateDetail(id: number) {
      return client.get<PrintTemplate>(API_ROUTES.printTemplate.detail(id))
    },

    /**
     * 创建打印模板
     */
    createTemplate(data: CreatePrintTemplateForm) {
      return client.post<PrintTemplate>(API_ROUTES.printTemplate.create, data)
    },

    /**
     * 更新打印模板
     */
    updateTemplate(id: number, data: UpdatePrintTemplateForm) {
      return client.put<PrintTemplate>(API_ROUTES.printTemplate.printTemplates(id), data)
    },

    /**
     * 删除打印模板
     */
    deleteTemplate(id: number) {
      return client.delete(API_ROUTES.printTemplate.delete(id))
    },

    /**
     * 设置默认打印模板
     */
    setDefaultTemplate(id: number) {
      return client.put<PrintTemplate>(API_ROUTES.printTemplate.setDefault(id))
    },

    /**
     * 获取打印模板版本号
     */
    getTemplateVersion() {
      return client.get<{ version: string; count: number }>(API_ROUTES.printTemplate.version, {
        ignoreCaceh: true,
      } as any)
    },
  }
}

export type PrinterApi = ReturnType<typeof createPrinterApi>
